import { View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  formatDraftWeekLine,
  programRoundLabel,
  roundTypeAt,
  type Draft,
  type DraftLineOptions,
} from "@heraion/shared";

import { ChipChoiceRow } from "../primitives/ChipChoiceRow";
import type { UiTheme } from "../theme";
import { RoundLineRow } from "./RoundLineRow";
import { RoundTypeBadge, useRoundTypeWords } from "../primitives/RoundTypeBadge";
import type { V3Navigation } from "./useV3Navigation";
import type { V3Styles } from "./v3Styles";

// The «Per round» lens of a day (#336, brief §7.2 «Seconda lente»): the
// round chips W1..Wn, then every exercise of the day with that round's line
// only, to check «is W3 right across the whole day?» in one screen. A row
// opens the exercise with that round expanded. The round is the heatmap's
// cursor, so a cell tap and a chip tap move the same thing. A deload or test
// round shows its badge under the chips (4d).
export function RoundLens({
  theme,
  styles,
  draft,
  nav,
  lineOptions,
  resolveName,
  groups,
  onAddToRound,
}: {
  theme: UiTheme;
  styles: V3Styles;
  draft: Draft;
  nav: V3Navigation;
  lineOptions: DraftLineOptions;
  resolveName: (id: string, snapshot?: string) => string;
  /** The day's superset labels (groupLabels), as the «Exercises» lens has them. */
  groups: Map<string, string>;
  /** «+» on an off row: the exercise joins this round. */
  onAddToRound?: (slotIdx: number, week: number) => void;
}) {
  const { t } = useTranslation();
  const sIdx = nav.dayIdx;
  const w = nav.roundIdx;
  const slots = draft.sessions[sIdx]?.slots ?? [];
  const off = t("editorV3.notInRound", { defaultValue: "Not in this round" });
  const rounds = Array.from({ length: draft.cycleWeeks }, (_, i) => i);
  const words = useRoundTypeWords();
  const type = roundTypeAt(draft, w);
  return (
    <View style={styles.rows}>
      <ChipChoiceRow
        theme={theme}
        scroll
        choices={rounds}
        selected={w}
        onSelect={nav.selectRound}
        label={(i) => programRoundLabel(i + 1)}
        chipA11yLabel={(i) => {
          const it = roundTypeAt(draft, i);
          return it === "normal"
            ? t("editorV3.roundChipA11y", { n: i + 1, defaultValue: "Round {{n}}" })
            : t("editorV3.roundChipTypedA11y", { n: i + 1, type: words[it], defaultValue: "Round {{n}}, {{type}}" });
        }}
        groupA11yLabel={t("editorV3.roundsA11y", { defaultValue: "Rounds" })}
      />
      {type === "normal" ? null : <RoundTypeBadge theme={theme} type={type} />}
      <View style={styles.ladder}>
        {slots.map((slot, slotIdx) => {
          const name = resolveName(slot.exerciseId, slot.exerciseName);
          const line = formatDraftWeekLine(slot, w, lineOptions);
          const tag = slot.group ? (groups.get(slot.group.id) ?? null) : null;
          const spoken = t("editorV3.roundA11y", {
            n: w + 1,
            line: line ?? off,
            defaultValue: "Round {{n}}: {{line}}",
          });
          return (
            <RoundLineRow
              key={slot.id}
              theme={theme}
              styles={styles}
              variant="lens"
              label={name}
              tag={tag}
              line={line}
              offText={off}
              onPress={() => nav.openExerciseAt(slot.id, w)}
              a11yLabel={[name, tag, spoken].filter(Boolean).join(", ")}
              onAdd={onAddToRound ? () => onAddToRound(slotIdx, w) : undefined}
              addA11yLabel={t("editorV3.addExerciseToRound", {
                name,
                n: w + 1,
                defaultValue: "Add {{name}} to W{{n}}",
              })}
            />
          );
        })}
      </View>
    </View>
  );
}
